import { action, computed, reaction, observable } from 'mobx';

import { Time } from 'services/base/time';
import { ResultKind } from 'services/base/types';
import { IPangolinDomain, HostMap, HistoryFilter } from 'services/pangolin/types';
import { HistoryService, HistoryFilterApplyResult } from 'services/pangolin/history_service';

import { CalendarRangeInputStore } from 'ui/standard/input/calendar_input';
import { ClockInputStore } from 'ui/standard/input/clock_input';

import { DirectionStore } from 'ui/history/direction_input';
import { ActionStore } from 'ui/history/action_input';
import { ProtocolInputStore } from 'ui/history/protocol_input';
import { SiteInputStore } from 'ui/history/site_input';
import { DeviceInputStore } from 'ui/history/device_input';
import { LocationInputStore } from 'ui/history/location_input';

import Messages from './filter_history_messages';

export class FilterHistoryStore {
  private readonly filter: HistoryFilter;
  dateRange: CalendarRangeInputStore;
  timeStart: ClockInputStore;
  timeEnd: ClockInputStore;
  directions: DirectionStore;
  actions: ActionStore;
  protocols: ProtocolInputStore;
  sites: SiteInputStore;
  devices: DeviceInputStore;
  locations: LocationInputStore;

  @observable.ref
  directionsError?: string;

  @observable.ref
  actionsError?: string;

  cleanStartDateTime: () => void;
  cleanEndDateTime: () => void;

  constructor(filter: HistoryFilter, hosts: HostMap) {
    const dateStart = new Date(
      filter.start.getFullYear(),
      filter.start.getMonth(),
      filter.start.getDate()
    );
    const dateEnd = new Date(
      filter.end.getFullYear(),
      filter.end.getMonth(),
      filter.end.getDate()
    );
    const timeStart = new Time(
      filter.start.getHours(),
      filter.start.getMinutes(),
      filter.start.getSeconds()
    );
    const timeEnd = new Time(
      filter.end.getHours(),
      filter.end.getMinutes(),
      filter.end.getSeconds()
    );

    this.filter = filter;

    this.dateRange = new CalendarRangeInputStore(dateStart, dateEnd);
    this.timeStart = new ClockInputStore(timeStart);
    this.timeEnd = new ClockInputStore(timeEnd);
    this.directions = new DirectionStore(filter.directions);
    this.actions = new ActionStore(filter.actions);

    this.protocols = new ProtocolInputStore(filter);
    this.sites = new SiteInputStore(filter);
    this.devices = new DeviceInputStore(filter, hosts);
    this.locations = new LocationInputStore(filter);

    this.cleanStartDateTime = reaction(
      () => this.startDateTime,
      () => {
        const startDateTime = this.startDateTime;
        const endDateTime = this.endDateTime;
        if (endDateTime < startDateTime) {
          this.dateRange.first.setDate(new Date(
            endDateTime.getFullYear(),
            endDateTime.getMonth(),
            endDateTime.getDate()
          ));
          this.timeStart.setTime(new Time(
            endDateTime.getHours(),
            endDateTime.getMinutes(),
            endDateTime.getSeconds()
          ));
        }
      },
      {
        fireImmediately: true,
      },
    );

    this.cleanEndDateTime = reaction(
      () => this.endDateTime,
      () => {
        const startDateTime = this.startDateTime;
        const endDateTime = this.endDateTime;
        if (endDateTime < startDateTime) {
          this.dateRange.final.setDate(new Date(
            startDateTime.getFullYear(),
            startDateTime.getMonth(),
            startDateTime.getDate()
          ));
          this.timeEnd.setTime(new Time(
            startDateTime.getHours(),
            startDateTime.getMinutes(),
            startDateTime.getSeconds()
          ));
        }
      },
      {
        fireImmediately: true,
      },
    );
  }

  clean(): void {
    this.cleanStartDateTime();
    this.cleanEndDateTime();
  }

  @computed get startDateTime(): Date {
    const dateStart = this.dateRange.first.committedDateObject;
    const timeStart = this.timeStart.committedTime;
    const startDateTime = new Date(
      dateStart.getFullYear(),
      dateStart.getMonth(),
      dateStart.getDate(),
      timeStart.hours,
      timeStart.minutes,
      timeStart.seconds
    );
    return startDateTime;
  }

  @computed get endDateTime(): Date {
    const dateEnd = this.dateRange.final.committedDateObject;
    const timeEnd = this.timeEnd.committedTime;
    const endDateTime = new Date(
      dateEnd.getFullYear(),
      dateEnd.getMonth(),
      dateEnd.getDate(),
      timeEnd.hours,
      timeEnd.minutes,
      timeEnd.seconds
    );
    return endDateTime;
  }

  @action.bound
  validate(filter: HistoryFilter): HistoryFilterApplyResult {
    let valid = true;

    const directions = [
      filter.directions.outbound,
      filter.directions.inbound,
      filter.directions.local,
    ];
    if (!directions.some((value) => value)) {
      this.directionsError = Messages.invalidDirections();
      valid = false;
    }

    const actions = [
      filter.actions.allowed,
      filter.actions.blocked,
    ];
    if (!actions.some((value) => value)) {
      this.actionsError = Messages.invalidActions();
      valid = false;
    }

    if (valid) {
      return { kind: ResultKind.OK, filter };
    } else {
      return {
        kind: ResultKind.USER_ERROR,
        directionsError: this.directionsError,
        actionsError: this.actionsError,
      };
    }
  }

  convertToHistoryFilter(): HistoryFilter {
    const startDateTime = this.startDateTime;
    const endDateTime = this.endDateTime;
    return {
      start: startDateTime, end: endDateTime,
      directions: { ...this.directions.values },
      actions: { ...this.actions.values },
      protocols: this.protocols.protocolFilters,
      sites: this.sites.siteFilters,
      devices: this.devices.hostIDs,
      locations: this.locations.countryCodes,
    };
  }

  @action.bound
  handleApply(domain: IPangolinDomain): HistoryFilterApplyResult {
    this.directionsError = undefined;
    this.actionsError = undefined;

    const filter = this.convertToHistoryFilter();

    const localResult = this.validate(filter);
    if (localResult.kind === ResultKind.USER_ERROR) {
      return localResult;
    }

    domain.historyFilter = filter;
    domain.historyFiltered = false;
    HistoryService.clearData(domain);

    return { kind: ResultKind.OK, filter };
  }
}
